import React, { useState, useEffect, useCallback } from 'react';
import { ChefHat, Plus, Trash2, Eye, Loader2, Sparkles, CheckSquare, Square, AlertCircle } from 'lucide-react';
import { recipesApi } from '../services/api';
import { aiService } from '../services/aiService';

const GL_BADGE = (gl) => {
  if (!gl) return null;
  const color = gl <= 5 ? 'green' : gl <= 10 ? 'yellow' : gl <= 20 ? 'orange' : 'red';
  const label = gl <= 5 ? 'Très faible' : gl <= 10 ? 'Faible' : gl <= 20 ? 'Modérée' : 'Élevée';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full bg-${color}-100 text-${color}-700 font-medium`}>
      CG {gl} · {label}
    </span>
  );
};

const RecipesPage = ({ onOpenRecipe }) => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [dishInput, setDishInput] = useState('');
  const [optimizeCG, setOptimizeCG] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);

  const loadRecipes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await recipesApi.list();
      setRecipes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRecipes(); }, [loadRecipes]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!dishInput.trim()) return;
    setGenerating(true);
    setGenError(null);
    try {
      const recipe = await aiService.generateRecipe(dishInput.trim(), optimizeCG);
      await recipesApi.create(recipe);
      setDishInput('');
      setOptimizeCG(false);
      setShowForm(false);
      await loadRecipes();
    } catch (err) {
      setGenError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette recette ?')) return;
    try {
      await recipesApi.remove(id);
      setRecipes(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const openRecipe = (id) => {
    if (onOpenRecipe) onOpenRecipe(id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Recettes Multi-Cuisson</h1>
              <p className="text-sm text-gray-500">{recipes.length} recette{recipes.length !== 1 ? 's' : ''} enregistrée{recipes.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl font-medium transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nouvelle recette</span>
          </button>
        </div>
      </div>

      {/* AI Generation Form */}
      {showForm && (
        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-orange-500" />
            <h2 className="font-semibold text-gray-800">Générer une recette IA</h2>
          </div>
          <form onSubmit={handleGenerate} className="space-y-4">
            <input
              type="text"
              value={dishInput}
              onChange={e => setDishInput(e.target.value)}
              placeholder="Ex : Curry de lentilles corail, Salade niçoise, Risotto…"
              className="w-full border border-orange-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-800"
              disabled={generating}
            />

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <button
                type="button"
                onClick={() => setOptimizeCG(v => !v)}
                className="text-orange-500 hover:text-orange-600"
              >
                {optimizeCG ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
              </button>
              <div>
                <span className="text-sm font-medium text-gray-700">Optimisation CG</span>
                <p className="text-xs text-gray-500">L'IA proposera des alternatives d'ingrédients et conseils pour réduire la charge glycémique</p>
              </div>
            </label>

            {genError && (
              <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {genError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={generating || !dishInput.trim()}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Génération…</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Générer</>
                )}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setGenError(null); }}
                className="px-6 py-3 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Recipe list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-700 font-medium">{error}</p>
          <button onClick={loadRecipes} className="mt-3 text-sm text-red-600 underline">Réessayer</button>
        </div>
      ) : recipes.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <ChefHat className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucune recette pour l'instant</p>
          <p className="text-sm text-gray-400 mt-1">Cliquez sur "Nouvelle recette" pour en créer une avec l'IA</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {recipes.map(recipe => {
            const gl = recipe.cg_data?.estimated_gl_per_serving;
            const servings = recipe.cg_data?.servings || 1;
            const ingredientCount = Array.isArray(recipe.ingredients) ? recipe.ingredients.length : 0;
            const applianceCount = Object.keys(recipe.appliance_configs || {}).length;

            return (
              <div key={recipe.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-gray-800 leading-snug">{recipe.title}</h3>
                  {GL_BADGE(gl)}
                </div>

                <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                  <span>{ingredientCount} ingrédient{ingredientCount !== 1 ? 's' : ''}</span>
                  <span>·</span>
                  <span>{servings} portion{servings !== 1 ? 's' : ''}</span>
                  {applianceCount > 0 && <><span>·</span><span>{applianceCount} appareil{applianceCount !== 1 ? 's' : ''}</span></>}
                </div>

                {recipe.base_instructions && (
                  <p className="text-sm text-gray-500 line-clamp-2">{recipe.base_instructions}</p>
                )}

                <div className="flex gap-2 mt-auto pt-2 border-t border-gray-50">
                  <button
                    onClick={() => openRecipe(recipe.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Eye className="w-4 h-4" /> Voir
                  </button>
                  <button
                    onClick={() => handleDelete(recipe.id)}
                    className="flex items-center justify-center p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecipesPage;
